import { authService } from '@/services/authService';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { z } from 'zod';

const schema = z.object({
  email: z.string().email('Invalid email address'),
});

type FormData = z.infer<typeof schema>;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    var(--primary-600) 0%,
    var(--primary-800) 100%
  );
  padding: 1rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  box-shadow: var(--shadow-xl);
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  h1 {
    color: var(--primary-600);
    font-size: 2rem;
    font-weight: 800;
    margin-bottom: 0.5rem;
    letter-spacing: 2px;
  }

  p {
    color: var(--gray-600);
    font-size: 0.875rem;
    margin: 0;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FormGroup = styled.div`
  position: relative;

  label {
    display: block;
    font-weight: 500;
    color: var(--gray-700);
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
  }

  input {
    width: 100%;
    padding: 0.625rem 0.75rem;
    padding-left: 2.5rem;
    border: 1px solid var(--gray-300);
    border-radius: 0.5rem;
    font-size: 0.875rem;
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: var(--primary-600);
      box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.1);
    }

    &::placeholder {
      color: var(--gray-400);
    }
  }
`;

const Icon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 2.25rem;
  color: var(--gray-400);
  width: 1.25rem;
  height: 1.25rem;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const ErrorMessage = styled.p`
  color: var(--error-600);
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const SubmitButton = styled.button`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.75rem 1rem;
  background: var(--primary-600);
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: var(--primary-700);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SuccessBox = styled.div`
  background-color: var(--success-50, #f0fdf4);
  border: 1px solid var(--success-200, #bbf7d0);
  border-radius: 0.5rem;
  padding: 1rem;
  text-align: center;

  p {
    color: var(--success-800, #166534);
    font-size: 0.875rem;
    margin: 0;
    line-height: 1.5;
  }
`;

const BackLink = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  font-size: 0.875rem;
  color: var(--gray-600);

  a {
    color: var(--primary-600);
    font-weight: 500;

    &:hover {
      color: var(--primary-700);
    }
  }
`;

const ForgotPassword: FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    try {
      await authService.forgotPassword(data.email);
      setSubmittedEmail(data.email);
      setSubmitted(true);
    } catch {
      setError('root', {
        message: 'Something went wrong. Please try again.',
      });
    }
  };

  return (
    <Container>
      <Card>
        <Logo>
          <h1>MARKETEER</h1>
          <p>Reset your password</p>
        </Logo>

        {submitted ? (
          <>
            <SuccessBox>
              <p>
                If an account exists for <strong>{submittedEmail}</strong>, you
                will receive a password reset email shortly.
              </p>
            </SuccessBox>
            <BackLink>
              <Link to="/login">Back to sign in</Link>
            </BackLink>
          </>
        ) : (
          <>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <FormGroup>
                <label htmlFor="email">Email address</label>
                <Icon>
                  <EnvelopeIcon />
                </Icon>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="Enter your email"
                  {...register('email')}
                />
                {errors.email && (
                  <ErrorMessage>{errors.email.message}</ErrorMessage>
                )}
              </FormGroup>

              {errors.root && (
                <ErrorMessage style={{ textAlign: 'center' }}>
                  {errors.root.message}
                </ErrorMessage>
              )}

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send reset link'}
              </SubmitButton>
            </Form>

            <BackLink>
              Remember your password? <Link to="/login">Sign in</Link>
            </BackLink>
          </>
        )}
      </Card>
    </Container>
  );
};

export default ForgotPassword;
